import './IssuesList.scss';

import { Action } from 'redux';
import { COPY_SPECIFIC } from '@sonar-software/sonar-ts-constants';
import { Issue } from './Issue';
import { NoData } from '@ui/NoData';
import { PaginatedList } from '@ui/lists';
import { Result } from 'antd';
import { take } from '@sonar-software/sonar-ts-lib';
import { useSelector } from 'react-redux';

import { AsyncSelectors, createAsyncSelectors } from '@lib/selectors';
import { GetIssuesRequest, GetIssuesResponse, issuesActions, IssueVisibilityType } from '@sonar-software/sonar-issues';
import { keyPaginationDefaultQueryParams, keyPaginationFallbackQueryParams } from '@ui/KeyPagination';
import React, { useEffect, useMemo } from 'react';
import { useAction, useQueryParams } from '@hooks';

function getLimitedIssues(paginatedIssues: GetIssuesResponse | null | undefined, limit: number | undefined) {
	if (!paginatedIssues || !limit) {
		return paginatedIssues;
	}

	return {
		...paginatedIssues,
		results: take(paginatedIssues.results, limit),
		totalCount: (paginatedIssues.totalCount ?? 0) > limit ? paginatedIssues : paginatedIssues.totalCount
	} as GetIssuesResponse;
}

interface MapEntry {
	action: (request: Partial<GetIssuesRequest>) => Action;
	empty: React.ReactNode;
	selectors: AsyncSelectors<GetIssuesResponse>;
}

const visiblityTypeMap: Record<IssueVisibilityType, MapEntry> = {
	[ IssueVisibilityType.Archived ]: {
		action: issuesActions.requestGetIssuesArchived,
		empty: <NoData description={ COPY_SPECIFIC.NO_ISSUES_ARCHIVED } />,
		selectors: createAsyncSelectors((state) => state.issues.archived.get)
	},
	[ IssueVisibilityType.Snoozed ]: {
		action: issuesActions.requestGetIssuesSnoozed,
		empty: <NoData description={ COPY_SPECIFIC.NO_ISSUES_SNOOZED } />,
		selectors: createAsyncSelectors((state) => state.issues.snoozed.get)
	},
	[ IssueVisibilityType.Visible ]: {
		action: issuesActions.requestGetIssuesVisible,
		empty:
	<Result
		status='success'
		subTitle={ COPY_SPECIFIC.NO_ISSUES_TO_REPORT }
		title={ COPY_SPECIFIC.EVERYTHING_LOOKS_GREAT } />,
		selectors: createAsyncSelectors((state) => state.issues.visible.get)
	}
};

export interface IssuesListProps {
	hideActions?: boolean;
	limit?: number;
	visibility: IssueVisibilityType;
}

export const IssuesList: React.FC<IssuesListProps> = ({ hideActions, limit, visibility }) => {
	const { queryParams, updateParams } = useQueryParams({
		defaultParams: keyPaginationDefaultQueryParams,
		fallbackParams: keyPaginationFallbackQueryParams
	});

	const typeMap = visiblityTypeMap[ visibility ] ?? visiblityTypeMap[ IssueVisibilityType.Visible ];

	const loadIssues = useAction(typeMap.action);
	const isLoading = useSelector(typeMap.selectors.isRequested);
	const paginatedIssues = useSelector(typeMap.selectors.data);

	const limitedIssues = useMemo(() => getLimitedIssues(paginatedIssues, limit), [ limit, paginatedIssues ]);

	useEffect(() => {
		loadIssues(queryParams);
	}, [ loadIssues, queryParams ]);

	return (
		<PaginatedList
			empty={ typeMap.empty }
			listClassName='issues-list'
			loading={ isLoading }
			onChange={ updateParams }
			queryParams={ queryParams }
			renderListItem={ (item) =>
				<Issue
					hideActions={ hideActions }
					issue={ item }
					key={ item.id }
					visibility={ visibility } /> }
			resource={ limitedIssues } />
	);
};
